import { z } from 'zod';
import { accessGate, configKey, editableText, entityId, isoDateTime, publishable, slug } from './primitives.js';

/**
 * CONFIGURACAO DE SITE
 *
 * Documento unico servido em GET /api/v1/site-config. O front-end nao possui
 * nenhuma marca, cor, texto, menu ou secao de home escritos em codigo: tudo
 * chega daqui. Trocar o SOLOTE pelo SOFAZENDA (Cap. 1.3) e servir outro
 * documento para outro dominio, reaproveitando o mesmo bundle React.
 */

/** Tokens de design. Viram custom properties CSS em tempo de execucao. */
/**
 * Aparencia padrao do portal.
 *
 * `system` segue o `prefers-color-scheme` de quem visita. Parece o mais
 * gentil, mas entrega a identidade da marca ao sistema operacional do
 * visitante: o mesmo site abre branco para um e preto para outro, e quem
 * escolheu a paleta nao decide o que aparece. Por isso o padrao e `light` —
 * uma escolha, nao uma omissao — e trocar e uma linha no painel.
 */
export const themeAppearance = z.enum(['light', 'dark', 'system']);
export type ThemeAppearance = z.infer<typeof themeAppearance>;

export const themeTokens = z.object({
  appearance: themeAppearance.default('light'),
  colors: z.record(z.string(), z.string()).default({}),
  fonts: z.record(z.string(), z.string()).default({}),
  radii: z.record(z.string(), z.string()).default({}),
  spacing: z.record(z.string(), z.string()).default({}),
  shadows: z.record(z.string(), z.string()).default({}),
  /**
   * Sobrescritas para o modo escuro. Apenas o que muda — o que nao estiver
   * aqui herda de `colors`.
   *
   * Existe porque um tema unico nao consegue atender os dois modos: um verde
   * que tem contraste sobre papel branco fica ilegivel sobre fundo preto. Sem
   * este par, publicar uma cor no painel apagaria o modo escuro inteiro.
   */
  darkColors: z.record(z.string(), z.string()).default({}),
});

export type ThemeTokens = z.infer<typeof themeTokens>;

export const brand = z.object({
  name: editableText,
  tagline: editableText.nullable().default(null),
  logoUrl: z.string().nullable().default(null),
  logoDarkUrl: z.string().nullable().default(null),
  faviconUrl: z.string().nullable().default(null),
  /** Prefixo do codigo do anuncio, ex.: "STL" -> STL-000184 (Cap. 2.2). */
  listingCodePrefix: z.string().min(2).max(6).default('STL'),
  supportWhatsapp: z.string().nullable().default(null),
  supportEmail: z.string().nullable().default(null),
});

export type Brand = z.infer<typeof brand>;

/**
 * Todo texto de interface tem chave. O front chama `t('home.hero.title')` e
 * jamais literais — assim o administrador reescreve a comunicacao inteira
 * pelo painel (Cap. 2.1, item "textos").
 */
export const textTokens = z.record(z.string(), z.string());
export type TextTokens = z.infer<typeof textTokens>;

const navItemBase = z.object({
  key: configKey,
  label: editableText,
  href: z.string(),
  icon: z.string().nullable().default(null),
  gate: accessGate.default({ roles: [], plans: [] }),
  external: z.boolean().default(false),
  publication: publishable.default({}),
});

export type NavItem = z.infer<typeof navItemBase> & { children: NavItem[] };
export type NavItemInput = z.input<typeof navItemBase> & { children?: NavItemInput[] };

/** Menus aninhados: por isso o par tipo-de-entrada / tipo-de-saida. */
export const navItem: z.ZodType<NavItem, z.ZodTypeDef, NavItemInput> = navItemBase.extend({
  children: z.lazy(() => z.array(navItem)).default([]),
});

/**
 * Secoes da home (Cap. 5.1). O admin adiciona, remove e reordena. `kind`
 * seleciona qual componente do registry renderiza; `props` e validado pelo
 * proprio componente no momento do render.
 */
export const pageSection = z.object({
  id: entityId,
  kind: z.enum([
    'hero_search',
    'natural_language_search', // "QUAL IMOVEL DESEJA?" (Cap. 5.2)
    'featured_listings',
    'recent_listings',
    'featured_cities',
    'categories',
    'plans_showcase', // vitrine de planos na home (Cap. 16)
    'advertiser_cta', // faixa de captacao de anunciante
    'banner',
    'content_cards',
    'cta',
    'stats',
    'faq',
    'html',
  ]),
  title: editableText.nullable().default(null),
  subtitle: editableText.nullable().default(null),
  props: z.record(z.string(), z.unknown()).default({}),
  gate: accessGate.default({ roles: [], plans: [] }),
  publication: publishable.default({}),
});

export type PageSection = z.infer<typeof pageSection>;

/** Paginas montadas no painel (institucionais, landing por cidade, juridicas). */
export const cmsPage = z.object({
  id: entityId,
  slug: z.string(),
  title: editableText,
  sections: z.array(pageSection).default([]),
  seo: z
    .object({
      title: editableText.nullable().default(null),
      description: editableText.nullable().default(null),
      canonical: z.string().nullable().default(null),
      noindex: z.boolean().default(false),
      ogImageUrl: z.string().nullable().default(null),
    })
    .default({}),
  /** Documento juridico versionado exige registro de aceite (Cap. 25.2). */
  legalDocument: z
    .object({
      key: configKey,
      version: z.string(),
      requiresAcceptance: z.boolean().default(false),
    })
    .nullable()
    .default(null),
  publication: publishable.default({}),
});

export type CmsPage = z.infer<typeof cmsPage>;

export const banner = z.object({
  id: entityId,
  /** Onde o banner pode aparecer. O front consulta por slot. */
  slot: z.string(),
  imageUrl: z.string(),
  mobileImageUrl: z.string().nullable().default(null),
  alt: editableText,
  href: z.string().nullable().default(null),
  /** Segmentacao simples: so exibe se a cidade/aptidao do contexto bater. */
  targeting: z
    .object({
      cities: z.array(z.string()).default([]),
      states: z.array(z.string()).default([]),
      aptitudes: z.array(z.string()).default([]),
    })
    .default({}),
  advertiserId: entityId.nullable().default(null),
  publication: publishable.default({}),
});

export type Banner = z.infer<typeof banner>;

/**
 * Flags de funcionalidade. Cobrem o roadmap do Cap. 37: a Fase 2 nasce
 * desligada em producao e e acesa pelo painel, sem release.
 */
export const featureFlag = z.object({
  key: configKey,
  enabled: z.boolean().default(false),
  description: editableText.nullable().default(null),
  /** Liberacao gradual: 0-100. */
  rolloutPercentage: z.number().int().min(0).max(100).default(100),
  gate: accessGate.default({ roles: [], plans: [] }),
});

export type FeatureFlag = z.infer<typeof featureFlag>;

export const siteConfig = z.object({
  /** Identifica o portal servido: 'solote' | 'sofazenda' | ... (Cap. 1.3). */
  tenant: slug,
  /** Muda a cada publicacao. O front usa como chave de cache. */
  version: z.string(),
  publishedAt: isoDateTime,
  brand,
  theme: themeTokens.default({}),
  texts: textTokens.default({}),
  navigation: z
    .object({
      header: z.array(navItem).default([]),
      footer: z.array(navItem).default([]),
      advertiserArea: z.array(navItem).default([]),
      admin: z.array(navItem).default([]),
    })
    .default({}),
  home: z.array(pageSection).default([]),
  pages: z.array(cmsPage).default([]),
  banners: z.array(banner).default([]),
  features: z.array(featureFlag).default([]),
  /** Chaves publicas de terceiros (mapas, analytics). Nunca segredos. */
  integrations: z.record(z.string(), z.string()).default({}),
  seo: z
    .object({
      titleTemplate: z.string().default('%s'),
      defaultDescription: editableText.default(''),
      /** Padroes de URL do Cap. 27.1, editaveis. */
      routePatterns: z.record(z.string(), z.string()).default({}),
    })
    .default({}),
});

export type SiteConfig = z.infer<typeof siteConfig>;
